import { useMemo } from 'react';

import { formatDate } from '../../utils/formatters';

import { ColumnDefinition } from '../../features/table-preferences/types';

import { FlattenedOrderItem } from './useCustomerOrders';

import { useCurrentUser } from '../../features/auth/api';

import fallbackProductImage from '../../assets/defeo-logo.png';

export type CustomerOrderAddress = {
  id: string;

  label?: string | null;

  fullName?: string | null;

  line1?: string | null;

  line2?: string | null;

  city?: string | null;

  state?: string | null;

  postalCode?: string | null;

  country?: string | null;
};

/*

 * Customer-facing status copy is intentionally separate from the admin labels.

 *

 * In particular, admins can continue to see APPROVED as "Accepted", while

 * customers see the friendlier lifecycle label "Processing".

 */

export const CUSTOMER_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',

  WAITLIST: 'Waitlist',

  APPROVED: 'Processing',

  ASSIGNED: 'Assigned',

  READY_FOR_PICKUP: 'Ready for Pickup',

  ENROUTE: 'Enroute',

  DELIVERED: 'Delivered',

  INVOICED: 'Invoiced',

  CUSTOMER_CANCELLED: 'Cancelled',

  PREPAY_REQUIRED: 'Prepay Required',

  PICKED_UP: 'Picked Up',

  ON_HOLD: 'Frozen',
};

/*

 * Customer-facing explanations shown when hovering the Status cell.

 *

 * INVENTORY is deliberately omitted because it is an internal-only status and

 * should not be explained/exposed to customers through the tooltip.

 */

const CUSTOMER_STATUS_TOOLTIPS: Record<string, string> = {
  APPROVED:
    "Your order has been accepted and is being processed. It isn't ready for delivery or pickup yet.",

  ASSIGNED: 'Your order has been assigned to a carrier for delivery.',

  CUSTOMER_CANCELLED: 'You cancelled this order.',

  DELIVERED: 'Your order has been delivered.',

  ENROUTE: 'Your order is on the way!',

  ON_HOLD:
    "Your account is on hold, and new orders can't be placed right now. Please contact a DeFeo Materials sales rep for details.",

  INVOICED: 'An invoice has been issued for this load.',

  PENDING:
    "We've received your order. A DeFeo Materials team member needs to accept it before processing begins.",

  PICKED_UP: "You've picked up this order.",

  PREPAY_REQUIRED: 'This order must be paid before we can begin processing it.',

  READY_FOR_PICKUP: 'Your order is ready for pickup!',

  WAITLIST:
    "This material is currently out of stock. You're on the waitlist, and we'll process your order when it's back.",
};

function customerStatusLabel(status: string): string {
  return CUSTOMER_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

function customerStatusTooltip(status: string): string | null {
  return CUSTOMER_STATUS_TOOLTIPS[status] ?? null;
}

interface UseCustomerOrderColumnsProps {
  addresses?: CustomerOrderAddress[];

  onCancelRequest: (orderId: string, itemId: string) => void;

  onViewNotes: (item: FlattenedOrderItem) => void;

  onShowOrder: (orderId: string) => void;
}

function formatDeliveryAddress(address: CustomerOrderAddress) {
  const label = address.label?.trim() || address.fullName?.trim() || 'Delivery address';

  const locality = [address.city?.trim(), address.state?.trim()].filter(Boolean).join(', ');

  const localityWithPostalCode = [locality, address.postalCode?.trim()].filter(Boolean).join(' ');

  const addressDetails = [address.line1?.trim(), address.line2?.trim(), localityWithPostalCode]

    .filter(Boolean)

    .join(', ');

  return (
    <div>
      <div style={{ fontWeight: 600 }}>{label}</div>

      <div className="muted tiny">{addressDetails || 'Address unavailable'}</div>
    </div>
  );
}

export function useCustomerOrderColumns({
  addresses = [],

  onCancelRequest,

  onViewNotes,

  onShowOrder,
}: UseCustomerOrderColumnsProps) {
  const { data: apiUser } = useCurrentUser();

  const apiUserObj = apiUser?.user ?? apiUser ?? null;

  const userType = apiUserObj?.type;

  const addressesById = useMemo(
    () => new Map(addresses.map((address) => [address.id, address])),

    [addresses]
  );

  const columns: ColumnDefinition<FlattenedOrderItem>[] = useMemo(
    () => [
      {
        id: 'load-number',

        description: 'Unique number for this load. Click to see the full order.',

        header: 'Load #',

        visible: true,

        defaultOrder: 0,

        sortable: true,

        sortKey: 'load-number',

        render: (row) => {
          const load = row.loadNumber || '—';
          const unreadNotes = row.unseenNoteCount ?? 0;

          return (
            <button
              type="button"
              className="btn btn--link"
              onClick={() => onShowOrder(row.orderId)}
              style={{
                padding: 0,
                border: 'none',
                background: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                position: 'relative',
              }}
              title={
                unreadNotes > 0
                  ? `${unreadNotes} new note${unreadNotes === 1 ? '' : 's'} from your rep`
                  : 'View order details'
              }
            >
              <span>{load}</span>

              {unreadNotes > 0 && (
                <span
                  aria-label={`${unreadNotes} new note${unreadNotes === 1 ? '' : 's'}`}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#dc2626',
                    boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.12)',
                    flex: '0 0 auto',
                  }}
                />
              )}
            </button>
          );
        },
      },

      {
        id: 'po-number',

        description: 'Your purchase order number for this load.',

        header: 'PO Number',

        visible: true,

        defaultOrder: 0.5,

        render: (row) => row.poNumber || '—',
      },

      {
        id: 'date',

        description: 'Date this order was placed.',

        header: 'Order Date',

        visible: true,

        defaultOrder: 1,

        sortable: true,

        sortKey: 'date',

        render: (row) => formatDate(row.createdAt),
      },

      {
        id: 'status',

        description:
          'Where this load is in its lifecycle. Notes appear here if it was cancelled or rejected.',

        header: 'Status',

        visible: true,

        defaultOrder: 2,

        sortable: true,

        sortKey: 'status',

        // The shared Table component shows this on hover for the entire cell.

        tooltip: (row) => customerStatusTooltip(row.status),

        render: (row) => {
          const hasNotes = Boolean(row.cancellationReason || row.rejectionReason);

          const statusLabel = customerStatusLabel(row.status);

          return (
            <div>
              <span className={`badge badge--${row.status.toLowerCase()}`}>{statusLabel}</span>

              {hasNotes && (
                <button type="button" className="note-chip" onClick={() => onViewNotes(row)}>
                  <span className="note-dot" aria-hidden="true" />
                  View notes
                </button>
              )}
            </div>
          );
        },
      },

      {
        id: 'delivery-address',

        description: 'Delivery address selected for this order.',

        header: 'Delivery Address',

        visible: true,

        defaultOrder: 3,

        sortable: false,

        render: (row) => {
          if (!row.deliveryAddressId) {
            return '—';
          }

          const address = addressesById.get(row.deliveryAddressId);

          return address ? formatDeliveryAddress(address) : '—';
        },
      },

      {
        id: 'product',

        description: 'Material being shipped.',

        header: 'Product',

        visible: true,

        defaultOrder: 4,

        sortable: true,

        sortKey: 'product',

        render: (row) => {
          const imageUrl = row.product?.imageUrl?.trim() || fallbackProductImage;

          return (
            <div className="prod-col">
              <img
                src={imageUrl}
                alt={row.product?.name || 'Product'}
                className="prod-col__img"
                onError={(event) => {
                  if (event.currentTarget.src !== fallbackProductImage) {
                    event.currentTarget.src = fallbackProductImage;
                  }
                }}
              />

              <div className="prod-col__text">
                <div className="prod-col__name">{row.product?.name || '—'}</div>
              </div>
            </div>
          );
        },
      },

      {
        id: 'actions',

        description:
          'Cancel the load while it can still be changed. Click the Load # for details, updates, documents, and notes.',
        header: 'Actions',
        className: 'ta-right',
        visible: true,
        defaultOrder: 6,
        sortable: false,
        render: (row) => {
          const canCancel = ['PENDING', 'WAITLIST', 'APPROVED'].includes(row.status);
          const isOtherUsersOrder =
            userType === 'SUBUSER' && row.userId !== apiUserObj?.firebaseUid;

          if (!canCancel) {
            return <span className="muted">—</span>;
          }

          const disabledTitle = isOtherUsersOrder ? 'Cannot edit order of another person' : '';

          return (
            <span title={disabledTitle} style={{ display: 'inline-block' }}>
              <button
                className="btn btn--ghost"
                onClick={() => onCancelRequest(row.orderId, row.id)}
                disabled={isOtherUsersOrder}
                style={isOtherUsersOrder ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                Cancel
              </button>
            </span>
          );
        },
      },
    ],

    [addressesById, onCancelRequest, onViewNotes, onShowOrder, userType, apiUserObj]
  );

  return { columns };
}
